// === INPACT:SLUITSTRAAT:BOILERPLATE:MODULE-SELECTION-PAGE:v1 ===

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useConfig,
  updateModulesConfig,
  markSetupStepCompleted,
  SettingsHeader,
  SettingsFooter,
} from '@sluitstraat/core';

interface ModuleInfo {
  id: string;
  name: string;
  description: string;
  detailedDescription: string;
  icon: string;
  category: 'Core' | 'Optional' | 'Experimental';
  dependencies: string[];
  enabled: boolean;
}

const ModuleSelectionPage: React.FC = () => {
  const { config, reloadConfig } = useConfig();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Module metadata
  const moduleMetadata: Record<string, Omit<ModuleInfo, 'id' | 'enabled'>> = {
    calculatorbox: {
      name: 'Calculator Box',
      description: 'Premium calculation engine',
      detailedDescription: 'Advanced calculation engine for insurance premium calculations, supporting multiple product types, risk assessments, and discount rules.',
      icon: '🧮',
      category: 'Optional',
      dependencies: []
    },
    clientservice: {
      name: 'Client Service',
      description: 'Customer management system',
      detailedDescription: 'Comprehensive customer data management including profiles, policies, claims history, and communication tracking.',
      icon: '👥',
      category: 'Optional',
      dependencies: []
    },
    jsonforms: {
      name: 'JSON Forms',
      description: 'Dynamic form builder (Core Module)',
      detailedDescription: 'Flexible form generation system based on JSON schemas with built-in validation, conditional fields, and custom renderers. This is a core module and cannot be disabled.',
      icon: '📝',
      category: 'Core',
      dependencies: []
    },
    productservice: {
      name: 'Product Service',
      description: 'Product catalog management',
      detailedDescription: 'Complete product lifecycle management including blueprints, versions, configurations, and multi-carrier support.',
      icon: '📦',
      category: 'Optional',
      dependencies: []
    },
    quotation: {
      name: 'Quotation',
      description: 'Quote generation system',
      detailedDescription: 'Generate, manage, and track insurance quotes with automated calculations, PDF exports, and customer delivery.',
      icon: '💼',
      category: 'Optional',
      dependencies: ['calculatorbox', 'productservice']
    },
    customer_portal: {
      name: 'Customer Portal',
      description: 'Self-service portal',
      detailedDescription: 'Customer-facing portal for policy management, claims submission, document access, and account settings.',
      icon: '🏠',
      category: 'Optional',
      dependencies: ['clientservice']
    },
  };

  const [selectedModules, setSelectedModules] = useState<Set<string>>(
    new Set(Object.keys(config.modules || {}).filter(id => config.modules[id]?.enabled))
  );

  const modules: ModuleInfo[] = Object.entries(config.modules || {}).map(([id, moduleConfig]) => ({
    id,
    ...moduleMetadata[id],
    enabled: moduleConfig?.enabled || false,
  }));

  const toggleModule = (moduleId: string) => {
    // Prevent toggling core modules
    if (moduleMetadata[moduleId]?.category === 'Core') {
      setNotification({
        type: 'error',
        message: `${moduleMetadata[moduleId]?.name} is a core module and cannot be disabled.`
      });
      return;
    }

    const newSelected = new Set(selectedModules);
    if (newSelected.has(moduleId)) {
      // Check if any other modules depend on this one
      const dependentModules = modules.filter(m =>
        m.dependencies.includes(moduleId) && newSelected.has(m.id)
      );

      if (dependentModules.length > 0) {
        setNotification({
          type: 'error',
          message: `Cannot disable ${moduleMetadata[moduleId]?.name}. It is required by: ${dependentModules.map(m => m.name).join(', ')}`
        });
        return;
      }

      newSelected.delete(moduleId);
    } else {
      newSelected.add(moduleId);

      // Auto-enable dependencies
      const module = moduleMetadata[moduleId];
      if (module?.dependencies) {
        module.dependencies.forEach(dep => newSelected.add(dep));
      }
    }

    setSelectedModules(newSelected);
    setNotification(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Note: We don't need to check for selectedModules.size === 0
    // because Core modules (like jsonforms) are always selected

    setIsSubmitting(true);
    setNotification(null);

    try {
      const modulesConfig = Object.keys(config.modules || {}).reduce((acc, id) => {
        acc[id] = { enabled: selectedModules.has(id) };
        return acc;
      }, {} as Record<string, { enabled: boolean }>);

      const success = await updateModulesConfig(modulesConfig);

      if (success) {
        // Mark the modules step as completed
        markSetupStepCompleted('modules');

        setNotification({ type: 'success', message: 'Module configuration saved successfully!' });

        // Reload the config to reflect the changes
        try {
          await reloadConfig();
        } catch (configError) {
          // Continue with navigation even if reload fails
        }

        // Navigate back to setup page
        setTimeout(() => {
          navigate('/settings', { replace: true });
        }, 1500);
      } else {
        setNotification({ type: 'error', message: 'Failed to save module configuration. Please try again.' });
      }
    } catch (error) {
      setNotification({ type: 'error', message: 'An error occurred while saving. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/settings');
  };

  const groupedModules = {
    Core: modules.filter(m => m.category === 'Core'),
    Optional: modules.filter(m => m.category === 'Optional'),
  };

  return (
    <>
      <SettingsHeader />
      <div style={styles.container}>
        <div style={styles.content}>
          <header style={styles.header}>
            <button onClick={handleCancel} style={styles.backButton}>
              ← Back to Setup
            </button>
            <h1 style={styles.title}>Module Selection</h1>
            <p style={styles.subtitle}>Choose which modules to activate for your Sluitstraat instance</p>
          </header>

        {notification && (
          <div style={{
            ...styles.notification,
            ...(notification.type === 'success' ? styles.notificationSuccess : styles.notificationError)
          }}>
            <span style={styles.notificationIcon}>
              {notification.type === 'success' ? '✓' : '⚠'}
            </span>
            {notification.message}
          </div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.summary}>
            <span style={styles.summaryText}>
              <strong>{selectedModules.size}</strong> of <strong>{modules.length}</strong> modules selected
            </span>
          </div>

          {Object.entries(groupedModules).map(([category, categoryModules]) => (
            categoryModules.length > 0 && (
              <div key={category} style={styles.category}>
                <h2 style={styles.categoryTitle}>
                  {category} Modules
                  {category === 'Core' && (
                    <span style={styles.categoryBadge}>Always Active</span>
                  )}
                </h2>
                {category === 'Core' && (
                  <p style={styles.categoryDescription}>
                    Core modules are essential for Sluitstraat functionality and cannot be disabled.
                  </p>
                )}

                <div style={styles.moduleGrid}>
                  {categoryModules.map((module) => {
                    const isSelected = selectedModules.has(module.id);
                    const isCoreModule = module.category === 'Core';
                    const hasUnmetDependencies = module.dependencies.some(dep => !selectedModules.has(dep));

                    return (
                      <div
                        key={module.id}
                        style={{
                          ...styles.moduleCard,
                          ...(isSelected ? styles.moduleCardSelected : {}),
                          ...(isCoreModule ? styles.moduleCardCore : {}),
                          ...(hasUnmetDependencies && isSelected ? styles.moduleCardWarning : {})
                        }}
                        onClick={() => !isCoreModule && toggleModule(module.id)}
                      >
                        <div style={styles.moduleCardHeader}>
                          <span style={styles.moduleIcon}>{module.icon}</span>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isCoreModule}
                            onChange={() => {}}
                            style={{
                              ...styles.checkbox,
                              ...(isCoreModule ? styles.checkboxDisabled : {})
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>

                        <h3 style={styles.moduleName}>{module.name}</h3>
                        <p style={styles.moduleDescription}>{module.description}</p>
                        <p style={styles.moduleDetailedDescription}>{module.detailedDescription}</p>

                        {module.dependencies.length > 0 && (
                          <div style={styles.dependencies}>
                            <span style={styles.dependenciesLabel}>Requires:</span>
                            <span style={styles.dependenciesList}>
                              {module.dependencies.map(dep => moduleMetadata[dep]?.name).join(', ')}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )
          ))}

          <div style={styles.infoBox}>
            <div style={styles.infoBoxIcon}>ℹ️</div>
            <div>
              <h3 style={styles.infoBoxTitle}>Module Dependencies</h3>
              <p style={styles.infoBoxText}>
                Some modules require other modules to function properly. When you select a module with dependencies,
                the required modules will be automatically enabled. You cannot disable a module if other enabled modules depend on it.
              </p>
            </div>
          </div>

          <div style={styles.actionBar}>
            <button
              type="button"
              onClick={handleCancel}
              style={styles.cancelButton}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                ...styles.saveButton,
                ...(isSubmitting ? styles.saveButtonDisabled : {})
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Module Selection'}
            </button>
          </div>
        </form>
      </div>
    </div>
    <SettingsFooter />
    </>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#F5F5F5',
    padding: '32px 20px',
  },
  content: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '32px',
  },
  backButton: {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    color: '#6b7280',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600' as const,
    cursor: 'pointer',
    marginBottom: '16px',
    transition: 'color 0.2s',
  },
  title: {
    margin: '0 0 8px 0',
    fontSize: '32px',
    fontWeight: '700' as const,
    color: '#1f2937',
  },
  subtitle: {
    margin: 0,
    fontSize: '16px',
    color: '#6b7280',
  },
  notification: {
    padding: '16px 20px',
    borderRadius: '8px',
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '14px',
    fontWeight: '600' as const,
  },
  notificationSuccess: {
    backgroundColor: '#d1fae5',
    color: '#065f46',
    border: '2px solid #10b981',
  },
  notificationError: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    border: '2px solid #ef4444',
  },
  notificationIcon: {
    fontSize: '18px',
    fontWeight: '700' as const,
  },
  form: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    padding: '32px',
  },
  summary: {
    backgroundColor: '#F5F5F5',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '32px',
    textAlign: 'center' as const,
  },
  summaryText: {
    fontSize: '16px',
    color: '#1f2937',
  },
  category: {
    marginBottom: '32px',
  },
  categoryTitle: {
    margin: '0 0 20px 0',
    fontSize: '20px',
    fontWeight: '600' as const,
    color: '#1f2937',
    paddingBottom: '12px',
    borderBottom: '2px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  categoryBadge: {
    fontSize: '12px',
    fontWeight: '600' as const,
    padding: '4px 12px',
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    borderRadius: '12px',
    textTransform: 'uppercase' as const,
  },
  categoryDescription: {
    margin: '0 0 16px 0',
    fontSize: '14px',
    color: '#6b7280',
    fontStyle: 'italic' as const,
  },
  moduleGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '20px',
  },
  moduleCard: {
    backgroundColor: '#F5F5F5',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    padding: '20px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  moduleCardSelected: {
    backgroundColor: '#dbeafe',
    borderColor: '#3b82f6',
  },
  moduleCardWarning: {
    borderColor: '#f59e0b',
    backgroundColor: '#fef3c7',
  },
  moduleCardCore: {
    backgroundColor: '#f0f9ff',
    borderColor: '#0284c7',
    cursor: 'not-allowed',
    opacity: 0.95,
  },
  moduleCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  moduleIcon: {
    fontSize: '32px',
  },
  checkbox: {
    width: '20px',
    height: '20px',
    cursor: 'pointer',
  },
  checkboxDisabled: {
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  moduleName: {
    margin: '0 0 8px 0',
    fontSize: '16px',
    fontWeight: '600' as const,
    color: '#1f2937',
  },
  moduleDescription: {
    margin: '0 0 8px 0',
    fontSize: '14px',
    color: '#6b7280',
    fontWeight: '500' as const,
  },
  moduleDetailedDescription: {
    margin: '0',
    fontSize: '13px',
    color: '#9ca3af',
    lineHeight: '1.5',
  },
  dependencies: {
    marginTop: '12px',
    padding: '8px 12px',
    backgroundColor: 'rgba(0,0,0,0.05)',
    borderRadius: '4px',
    fontSize: '12px',
  },
  dependenciesLabel: {
    fontWeight: '600' as const,
    color: '#6b7280',
    marginRight: '6px',
  },
  dependenciesList: {
    color: '#9ca3af',
  },
  infoBox: {
    backgroundColor: '#eff6ff',
    border: '2px solid #3b82f6',
    borderRadius: '8px',
    padding: '20px',
    marginTop: '32px',
    marginBottom: '32px',
    display: 'flex',
    gap: '16px',
  },
  infoBoxIcon: {
    fontSize: '24px',
    flexShrink: 0,
  },
  infoBoxTitle: {
    margin: '0 0 8px 0',
    fontSize: '16px',
    fontWeight: '600' as const,
    color: '#1e40af',
  },
  infoBoxText: {
    margin: '0',
    fontSize: '14px',
    color: '#1e40af',
    lineHeight: '1.6',
  },
  actionBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    paddingTop: '24px',
    borderTop: '1px solid #e5e7eb',
  },
  cancelButton: {
    padding: '12px 24px',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600' as const,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  saveButton: {
    padding: '12px 32px',
    backgroundColor: 'var(--primary-color, #056666)',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600' as const,
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  },
  saveButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
};

export default ModuleSelectionPage;
